import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import api from '../api';
import { dispatchCompsChangeEvent } from '../compsEventSystem';

interface Comp {
  id?: string;
  address: string;
  sale_date: string;
  sale_price: number;
  sqft: number;
  price_per_sqft: number;
}

interface EditCompModalProps {
  comp: Comp | null;
  onClose: () => void;
}

const EditCompModal: React.FC<EditCompModalProps> = ({ comp, onClose }) => {
  const [address, setAddress] = useState('');
  const [saleDate, setSaleDate] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [sqft, setSqft] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (comp) {
      setAddress(comp.address);
      setSaleDate(comp.sale_date ? new Date(comp.sale_date).toISOString().split('T')[0] : '');
      setSalePrice(comp.sale_price.toString());
      setSqft(comp.sqft.toString());
    }
  }, [comp]);

  const handleSave = async () => {
    if (!comp) return;

    try {
      await api.put(`/comps/${comp.id}`, {
        address: address,
        sale_date: saleDate,
        sale_price: parseFloat(salePrice),
        sqft: parseFloat(sqft),
      });
      setMessage('Comparable sale updated successfully!');
      dispatchCompsChangeEvent();
      onClose();
    } catch (error: any) {
      console.error('Error updating comparable sale:', error);
      setMessage(error.response?.data?.detail || 'Failed to update comparable sale.');
    }
  };

  if (!comp) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-bg-secondary p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-2xl font-bold text-text-primary mb-4">{t("editCompModal.k1")}</h2>
        {message && <p className="text-danger mb-4">{message}</p>}
        <div className="mb-4">
          <label htmlFor="address" className="block text-text-secondary text-sm font-bold mb-2">{t("editCompModal.k2")}</label>
          <input
            type="text"
            id="address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="saleDate" className="block text-text-secondary text-sm font-bold mb-2">{t("editCompModal.k3")}</label>
          <input
            type="date"
            id="saleDate"
            value={saleDate}
            onChange={(e) => setSaleDate(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="salePrice" className="block text-text-secondary text-sm font-bold mb-2">{t("editCompModal.k4")}</label>
          <input
            type="number"
            id="salePrice"
            value={salePrice}
            onChange={(e) => setSalePrice(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="sqft" className="block text-text-secondary text-sm font-bold mb-2">{t("editCompModal.k5")}</label>
          <input
            type="number"
            id="sqft"
            value={sqft}
            onChange={(e) => setSqft(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="flex justify-end gap-4">
          <button onClick={onClose} className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600">
            {t("editCompModal.k6")} </button>
          <button onClick={handleSave} className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark">
            {t("editCompModal.k7")} </button>
        </div>
      </div>
    </div>
  );
};

export default EditCompModal;
